/**
 * OlyticThinkingIndicator — Sub-agent VVVV Track 1 (2026-07-29),
 * reshaped by Sub-agent DDDDD (2026-07-29).
 *
 * Olytic-native streaming animation. Replaces the generic three-dot pulse
 * (previously rendered via .chat-dots CSS + PulsingDot component) with a
 * subtle "traveling wave" indicator that reads as brand + trust + noise-
 * reduction.
 *
 * Visual shape (Sub-agent DDDDD reshape — Option B, wave-only):
 *
 *     ~~~~~~~~~~
 *
 *   - Full-width wave path spanning the SVG viewport, stroke-dashoffset
 *     animates a "ping traveling outward" motion. Ease-in-out, ~2s cycle.
 *   - Uses `--olytic-teal` so the animation inherits the workspace brand
 *     tint (BrandThemeProvider swaps this per account).
 *
 * The prior shape carried a leading solid teal dot in front of the wave;
 * with the dot dropped the indicator reads as a clean traveling wave that
 * ties directly to the Olytic brand-mark's wave imagery (IconWave). The
 * dot was pattern-matching as an unintended anatomical read (Ean 2026-07-29);
 * removing it also simplifies the rendered atoms from 2 to 1.
 *
 * Two size variants:
 *   - 'inline' (default): 24×12 SVG, meant to sit alongside a rotating
 *     label ("Thinking… / Weighing options…") inside the ActivityTimeline
 *     header and the MessageItem ThinkingPlaceholder.
 *   - 'compact': 16×10 SVG — used as the pending-event row indicator on
 *     the timeline body.
 *
 * Preserves RRRR Track 3.1: the rotating vocabulary label continues to be
 * rendered by the caller. This component owns ONLY the animated shape
 * on the left of the label. The "expandable-after-done" collapse is
 * handled entirely by ActivityTimeline — this component sits inside the
 * live-state render and simply disappears when the caller unmounts it.
 *
 * CSS keyframes live in apps/ui/src/styles/app.css. `.olytic-thinking-dot`
 * class is preserved in the stylesheet for backward-compat (harmless — no
 * element carries it after this reshape) alongside the still-live
 * `.olytic-thinking-wave` animation.
 */

import type { CSSProperties } from 'react';

export type OlyticThinkingIndicatorVariant = 'inline' | 'compact';

interface Props {
  variant?: OlyticThinkingIndicatorVariant;
  /** Optional style override (color, margin). The default stroke + fill
   *  inherit from currentColor via `var(--olytic-teal)` so BrandTheme
   *  swaps apply automatically. */
  style?: CSSProperties;
}

export function OlyticThinkingIndicator({
  variant = 'inline',
  style,
}: Props): React.ReactElement {
  const isCompact = variant === 'compact';
  const width = isCompact ? 16 : 24;
  const height = isCompact ? 10 : 12;
  // Sub-agent DDDDD (2026-07-29) — wave-only reshape. Path spans the full
  // viewport (small 1px inset on each side so stroke ends don't clip);
  // four consecutive quarter-period reflections give two full sinusoid
  // periods across the width. Amplitude scales with variant so the compact
  // variant reads at a subtler intensity.
  const midY = height / 2;
  const amp = isCompact ? 1.4 : 1.8;
  const pathStart = 1;
  const pathEnd = width - 1;
  const period = (pathEnd - pathStart) / 2;
  const wavePath =
    `M ${pathStart} ${midY} ` +
    `q ${period / 4} -${amp}, ${period / 2} 0 ` +
    `t ${period / 2} 0 ` +
    `t ${period / 2} 0 ` +
    `t ${period / 2} 0`;

  return (
    <span
      aria-hidden="true"
      className="olytic-thinking-indicator"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        color: 'var(--olytic-teal)',
        ...(style ?? {}),
      }}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block', overflow: 'visible' }}
      >
        <path
          className="olytic-thinking-wave"
          d={wavePath}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}
